/**
 * Archival & Records Management API services
 * Handles legal holds, archive transfers, and disposition schedules
 */
import { api } from './api';
import type { ApiResponse } from '@/types';
import type { PaginatedApiResponse, PaginatedMeta } from './institution';

// Helper to convert snake_case to camelCase
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Transform object keys from snake_case to camelCase
function transformKeys<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item)) as T;
  }
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = snakeToCamel(key);
      result[camelKey] = transformKeys(value);
    }
    return result as T;
  }
  return obj as T;
}

async function transformedGet<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
  const response = await api.get<unknown>(endpoint, params);
  if (response.success && response.data) {
    return {
      ...response,
      data: transformKeys<T>(response.data),
    };
  }
  return response as ApiResponse<T>;
}

async function transformedGetWithMeta<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<PaginatedApiResponse<T>> {
  const response = await api.get<unknown>(endpoint, params);
  if (response.success && response.data) {
    const rawMeta = response.meta as PaginatedMeta | undefined;
    return {
      ...response,
      data: transformKeys<T>(response.data),
      meta: rawMeta,
    };
  }
  return response as PaginatedApiResponse<T>;
}

// ============================================
// Legal Hold Types
// ============================================

export interface LegalHold {
  id: string;
  mdaId: string;
  holdName: string;
  caseReference: string | null;
  legalMatter: string;
  scope: 'pfile' | 'document' | 'staff_all' | 'department';
  pfileId: string | null;
  documentId: string | null;
  affectedStaffId: string | null;
  departmentId: string | null;
  status: 'active' | 'released' | 'expired';
  startDate: string;
  endDate: string | null;
  notes: string | null;
  createdBy: string;
  releasedBy: string | null;
  releaseNotes: string | null;
  releaseDate: string | null;
  createdAt: string;
  updatedAt: string;
  creator?: { firstName: string; lastName: string };
  releaser?: { firstName: string; lastName: string } | null;
  pfile?: { id: string; fileNumber: string; staff: { firstName: string; lastName: string } } | null;
  document?: { id: string; title: string } | null;
  affectedStaff?: { firstName: string; lastName: string } | null;
  department?: { name: string } | null;
}

export interface LegalHoldStats {
  total: number;
  active: number;
  released: number;
  expired: number;
  byScope: { scope: string; count: number }[];
}

export interface CreateLegalHoldInput {
  holdName: string;
  caseReference?: string;
  legalMatter: string;
  scope: 'pfile' | 'document' | 'staff_all' | 'department';
  pfileId?: string;
  documentId?: string;
  affectedStaffId?: string;
  departmentId?: string;
  startDate: string;
  endDate?: string;
  notes?: string;
}

// ============================================
// Archive Transfer Types
// ============================================

export interface ArchiveTransfer {
  id: string;
  mdaId: string;
  transferNumber: string;
  transferType: 'praad' | 'mda_archive' | 'destruction';
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'in_transit' | 'completed' | 'cancelled';
  initiatedBy: string;
  approvedBy: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  completedAt: string | null;
  praadReference: string | null;
  accessionNumber: string | null;
  receivingOfficer: string | null;
  receivingOfficerTitle: string | null;
  receiptDate: string | null;
  destructionCertificateNumber: string | null;
  destructionMethod: string | null;
  destructionWitness: string | null;
  destructionDate: string | null;
  totalItems: number;
  verifiedItems: number;
  notes: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  initiator?: { firstName: string; lastName: string };
  approver?: { firstName: string; lastName: string } | null;
}

export interface ArchiveTransferItem {
  id: string;
  transferId: string;
  mdaId: string;
  itemType: 'pfile' | 'document';
  pfileId: string | null;
  documentId: string | null;
  isVerified: boolean;
  verifiedBy: string | null;
  verifiedAt: string | null;
  verificationNotes: string | null;
  archiveBox: string | null;
  boxSequence: number | null;
  createdAt: string;
  pfile?: { fileNumber: string; staff: { firstName: string; lastName: string } } | null;
  document?: { title: string; referenceNumber: string | null } | null;
  verifier?: { firstName: string; lastName: string } | null;
}

export interface ArchiveTransferStats {
  total: number;
  draft: number;
  submitted: number;
  approved: number;
  inTransit: number;
  completed: number;
  rejected: number;
  byType: { transferType: string; count: number }[];
}

export interface CreateArchiveTransferInput {
  transferType: 'praad' | 'mda_archive' | 'destruction';
  notes?: string;
}

export interface AddTransferItemInput {
  itemType: 'pfile' | 'document';
  pfileId?: string;
  documentId?: string;
  archiveBox?: string;
  boxSequence?: number;
}

// ============================================
// Disposition Types
// ============================================

export interface DispositionSchedule {
  id: string;
  mdaId: string;
  pfileId: string | null;
  documentId: string | null;
  dispositionAction: 'archive_praad' | 'archive_mda' | 'destroy' | 'review' | 'permanent_retention';
  scheduledDate: string;
  status: 'scheduled' | 'approved' | 'executed' | 'deferred' | 'cancelled';
  approvedBy: string | null;
  approvedAt: string | null;
  executedAt: string | null;
  executedBy: string | null;
  transferId: string | null;
  isOnHold: boolean;
  holdId: string | null;
  retentionRuleId: string | null;
  retentionRuleName: string | null;
  retentionPeriodYears: number | null;
  retentionTrigger: string | null;
  triggerDate: string | null;
  deferralCount: number;
  lastDeferredAt: string | null;
  lastDeferredBy: string | null;
  deferralReason: string | null;
  cancellationReason: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  pfile?: { fileNumber: string; staff: { firstName: string; lastName: string } } | null;
  document?: { title: string; referenceNumber: string | null } | null;
  creator?: { firstName: string; lastName: string };
  hold?: { holdName: string; caseReference: string | null } | null;
}

export interface DispositionStats {
  total: number;
  scheduled: number;
  approved: number;
  executed: number;
  deferred: number;
  cancelled: number;
  onHold: number;
  byAction: { action: string; count: number }[];
  upcomingThisMonth: number;
  overdueCount: number;
}

export interface CreateDispositionInput {
  pfileId?: string;
  documentId?: string;
  dispositionAction: 'archive_praad' | 'archive_mda' | 'destroy' | 'review' | 'permanent_retention';
  scheduledDate: string;
  retentionRuleId?: string;
  retentionRuleName?: string;
  retentionPeriodYears?: number;
  retentionTrigger?: string;
  triggerDate?: string;
  notes?: string;
}

export interface DispositionCalculation {
  pfileId: string;
  fileNumber: string;
  staffName: string;
  retentionRule: string;
  retentionPeriodYears: number;
  triggerDate: string | null;
  triggerType: string;
  calculatedDispositionDate: string | null;
  recommendedAction: string;
  isOnHold: boolean;
  holdDetails?: { holdName: string; caseReference: string | null } | null;
}

// ============================================
// Legal Holds API
// ============================================

export const archivalLegalHolds = {
  list: (params?: {
    page?: number;
    perPage?: number;
    status?: string;
    scope?: string;
    search?: string;
  }) => transformedGetWithMeta<LegalHold[]>('/v1/legal-holds', params as Record<string, string | number | boolean | undefined>),

  getById: (id: string) => transformedGet<LegalHold>(`/v1/legal-holds/${id}`),

  getStats: () => transformedGet<LegalHoldStats>('/v1/legal-holds/stats'),

  checkPFile: (pfileId: string) => transformedGet<{ isOnHold: boolean; holds: LegalHold[] }>(`/v1/legal-holds/check/pfile/${pfileId}`),

  checkDocument: (documentId: string) => transformedGet<{ isOnHold: boolean; holds: LegalHold[] }>(`/v1/legal-holds/check/document/${documentId}`),

  create: async (input: CreateLegalHoldInput): Promise<ApiResponse<LegalHold>> => {
    const response = await api.post<unknown>('/v1/legal-holds', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<LegalHold>(response.data) };
    }
    return response as ApiResponse<LegalHold>;
  },

  release: async (id: string, releaseNotes?: string): Promise<ApiResponse<LegalHold>> => {
    const response = await api.post<unknown>(`/v1/legal-holds/${id}/release`, { releaseNotes });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<LegalHold>(response.data) };
    }
    return response as ApiResponse<LegalHold>;
  },

  extend: async (id: string, endDate: string, notes?: string): Promise<ApiResponse<LegalHold>> => {
    const response = await api.put<unknown>(`/v1/legal-holds/${id}/extend`, { endDate, notes });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<LegalHold>(response.data) };
    }
    return response as ApiResponse<LegalHold>;
  },
};

// ============================================
// Archive Transfers API
// ============================================

export const archivalTransfers = {
  list: (params?: {
    page?: number;
    perPage?: number;
    status?: string;
    transferType?: string;
    search?: string;
  }) => transformedGetWithMeta<ArchiveTransfer[]>('/v1/archive-transfers', params as Record<string, string | number | boolean | undefined>),

  getById: (id: string) => transformedGet<ArchiveTransfer>(`/v1/archive-transfers/${id}`),

  getStats: () => transformedGet<ArchiveTransferStats>('/v1/archive-transfers/stats'),

  getItems: (id: string) => transformedGet<ArchiveTransferItem[]>(`/v1/archive-transfers/${id}/items`),

  create: async (input: CreateArchiveTransferInput): Promise<ApiResponse<ArchiveTransfer>> => {
    const response = await api.post<unknown>('/v1/archive-transfers', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransfer>(response.data) };
    }
    return response as ApiResponse<ArchiveTransfer>;
  },

  addItem: async (transferId: string, input: AddTransferItemInput): Promise<ApiResponse<ArchiveTransferItem>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${transferId}/items`, input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransferItem>(response.data) };
    }
    return response as ApiResponse<ArchiveTransferItem>;
  },

  removeItem: (transferId: string, itemId: string) => api.delete(`/v1/archive-transfers/${transferId}/items/${itemId}`),

  verifyItem: async (transferId: string, itemId: string, notes?: string): Promise<ApiResponse<ArchiveTransferItem>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${transferId}/items/${itemId}/verify`, { notes });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransferItem>(response.data) };
    }
    return response as ApiResponse<ArchiveTransferItem>;
  },

  submit: async (id: string): Promise<ApiResponse<ArchiveTransfer>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${id}/submit`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransfer>(response.data) };
    }
    return response as ApiResponse<ArchiveTransfer>;
  },

  approve: async (id: string): Promise<ApiResponse<ArchiveTransfer>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${id}/approve`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransfer>(response.data) };
    }
    return response as ApiResponse<ArchiveTransfer>;
  },

  reject: async (id: string, reason: string): Promise<ApiResponse<ArchiveTransfer>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${id}/reject`, { reason });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransfer>(response.data) };
    }
    return response as ApiResponse<ArchiveTransfer>;
  },

  markInTransit: async (id: string): Promise<ApiResponse<ArchiveTransfer>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${id}/in-transit`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransfer>(response.data) };
    }
    return response as ApiResponse<ArchiveTransfer>;
  },

  recordPraadReceipt: async (id: string, input: {
    praadReference: string;
    accessionNumber?: string;
    receivingOfficer?: string;
    receivingOfficerTitle?: string;
    receiptDate?: string;
  }): Promise<ApiResponse<ArchiveTransfer>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${id}/praad-receipt`, input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransfer>(response.data) };
    }
    return response as ApiResponse<ArchiveTransfer>;
  },

  recordDestruction: async (id: string, input: {
    destructionCertificateNumber: string;
    destructionMethod: string;
    destructionWitness?: string;
    destructionDate?: string;
  }): Promise<ApiResponse<ArchiveTransfer>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${id}/destruction`, input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransfer>(response.data) };
    }
    return response as ApiResponse<ArchiveTransfer>;
  },

  complete: async (id: string): Promise<ApiResponse<ArchiveTransfer>> => {
    const response = await api.post<unknown>(`/v1/archive-transfers/${id}/complete`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ArchiveTransfer>(response.data) };
    }
    return response as ApiResponse<ArchiveTransfer>;
  },

  getDestructionCertificate: (id: string) => transformedGet<{
    certificateNumber: string;
    transferNumber: string;
    destructionDate: string;
    destructionMethod: string;
    witness: string;
    items: { type: string; identifier: string; title: string }[];
    mdaName: string;
    issuedBy: string;
    issuedAt: string;
  }>(`/v1/archive-transfers/${id}/destruction-certificate`),
};

// ============================================
// Disposition Schedules API
// ============================================

export const archivalDispositions = {
  list: (params?: {
    page?: number;
    perPage?: number;
    status?: string;
    action?: string;
    dateFrom?: string;
    dateTo?: string;
    onHold?: boolean;
  }) => transformedGetWithMeta<DispositionSchedule[]>('/v1/dispositions/schedules', params as Record<string, string | number | boolean | undefined>),

  getById: (id: string) => transformedGet<DispositionSchedule>(`/v1/dispositions/${id}`),

  getStats: () => transformedGet<DispositionStats>('/v1/dispositions/stats'),

  getUpcoming: (days?: number) => transformedGet<DispositionSchedule[]>('/v1/dispositions/upcoming', { days }),

  getOverdue: () => transformedGet<DispositionSchedule[]>('/v1/dispositions/overdue'),

  calculateForPFile: (pfileId: string) => transformedGet<DispositionCalculation>(`/v1/dispositions/calculate/${pfileId}`),

  create: async (input: CreateDispositionInput): Promise<ApiResponse<DispositionSchedule>> => {
    const response = await api.post<unknown>('/v1/dispositions', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DispositionSchedule>(response.data) };
    }
    return response as ApiResponse<DispositionSchedule>;
  },

  defer: async (id: string, newDate: string, reason: string): Promise<ApiResponse<DispositionSchedule>> => {
    const response = await api.post<unknown>(`/v1/dispositions/${id}/defer`, { newDate, reason });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DispositionSchedule>(response.data) };
    }
    return response as ApiResponse<DispositionSchedule>;
  },

  approve: async (id: string): Promise<ApiResponse<DispositionSchedule>> => {
    const response = await api.post<unknown>(`/v1/dispositions/${id}/approve`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DispositionSchedule>(response.data) };
    }
    return response as ApiResponse<DispositionSchedule>;
  },

  execute: async (id: string, transferId?: string): Promise<ApiResponse<DispositionSchedule>> => {
    const response = await api.post<unknown>(`/v1/dispositions/${id}/execute`, transferId ? { transferId } : {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DispositionSchedule>(response.data) };
    }
    return response as ApiResponse<DispositionSchedule>;
  },

  cancel: async (id: string, reason: string): Promise<ApiResponse<DispositionSchedule>> => {
    const response = await api.post<unknown>(`/v1/dispositions/${id}/cancel`, { reason });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DispositionSchedule>(response.data) };
    }
    return response as ApiResponse<DispositionSchedule>;
  },
};
